/* eslint-disable max-lines */
// deps
import { useCallback, useContext, useEffect, useState } from "react";

// internal-deps
import { AmazonSdkChargeStatusResponseData } from "@swiggy-private/native-bridge";
import { TOAST_TYPE } from "@swiggy-private/payment-core";

// hooks
import { captureError, useMountedRef, format } from "@swiggy-private/core-ui";

// helpers
import {
    getJuspayBackURL,
    processOrder,
    ProcessOrderReturnType,
    shouldThrottleProcessOrderForSavedCard,
} from "@payment-client/helpers/OrderHelper";
import { getPaymentsCheckoutState, getPlaceOrderPaymentMethodName } from "@payment-client/helpers/PaymentHelper";
import { resolveClientAuthTokenData } from "@payment-client/helpers/JuspayHelpers";
import { saveProcessOrderState } from "@payment-client/helpers/CheckoutStateHelper";
import { ORDER_FAIL_INSTANCE, sendOrderFailedEvent } from "@payment-client/helpers/OrderFailedHelper";

// contexts
import {
    ApiContext,
    CheckoutActionTypes,
    CheckoutContextValue,
    ConfigContext,
    getBridgeSdk,
    recordNREvent,
    NEWRELIC_TABLES,
    BLAZE_EVENTS,
    AnalyticsContext,
} from "@swiggy-private/checkout-core";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { JuspayContext } from "@payment-client/contexts/JuspayContext";

// utils
import { hideNativeHeader, showNativeAlert, showNativeHeader } from "@payment-client/utils/Native";
import Blaze from "@swiggy-private/blaze.js";
import { shouldPollForTxnStatus } from "@payment-client/helpers/PollingHelpers";

// constants
import { PAYMENT_BACK_ALERT_OPTIONS, MWEB_UPI_APP_REDIRECT_FAILURE_MESSAGE } from "@payment-client/constants/TextDict";
import { UPI_NB_STATUS } from "@payment-client/interfaces/Checkout";
// apis
import { aPayValidateTransactionPublic } from "@payment-client/apis/Wallet";
import { isPaymentMethodUpiAndPaymentCodeUpiIntent } from "@payment-client/helpers/UpiHelper";
import { aPaySignAndEncryptPublic } from "@payment-client/apis/publicWallet";

// analytics
import { upiPaymentFailedImpressionEvent } from "@payment-client/analytics/events/PaymentFailed";
import { mwebUpiIntentRedirectFailureImpressionEvent } from "@payment-client/analytics/events/UpiIntent";
import { isBusinessLineCBCC } from "@payment-client/helpers/Misc";
import { redirectToCBCC } from "@payment-client/helpers/CBCCHelper";
import { AMAZON_PAY_NB_STATES, APAY_NB_CALL_STATUS } from "@payment-client/constants/AmazonPay";
import { recordNativeBridgeCall } from "@payment-client/helpers/AmazonPayNREvents";
import { BOOLEAN_AS_STRING } from "@swiggy-private/payment-types";
import { isEmpty } from "@swiggy-private/js-utils";

const useProcessOrder = (checkoutContext: CheckoutContextValue): [boolean] => {
    const isMountedRef = useMountedRef();
    const configValue = useContext(ConfigContext);
    const apiContextValue = useContext(ApiContext);
    const { clientAuthTokenData: rawClientAuthTokenData } = useContext(JuspayContext);
    const { paymentResponse } = useContext(ShowPaymentContext);
    const clientAuthTokenData = resolveClientAuthTokenData(rawClientAuthTokenData, paymentResponse?.tenant);

    const { impressionEvent } = useContext(AnalyticsContext);
    const [processingOrder, setProcessingOrder] = useState(false);

    const { checkoutState, setCheckoutState } = checkoutContext;
    const apiHost = apiContextValue.host.unwrap();

    const reset = useCallback((): void => {
        // Handled Swiggy Money & Regular Payments Checkout State
        recordNREvent({
            eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
            type: "PROCESS_ORDER_RESET",
        });
        const paymentsState = getPaymentsCheckoutState();
        setCheckoutState({ type: paymentsState });
        setProcessingOrder(false);
    }, [setCheckoutState]);

    useEffect(() => {
        if (isMountedRef.current && !processingOrder && checkoutState.type === CheckoutActionTypes.PROCESS_ORDER) {
            setProcessingOrder(true);
        }
    }, [checkoutState.type, isMountedRef, processingOrder]);

    useEffect(() => {
        recordNREvent({
            eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
            eventSubType: "useProcessOrder_onload",
            isMountedRef: String(isMountedRef.current),
            processingOrder: String(processingOrder),
            checkoutStateType: checkoutState.type,
            type: "PROCESS_ORDER",
        });
        // is mounted check..
        if (
            !isMountedRef.current ||
            !processingOrder ||
            checkoutState.type !== CheckoutActionTypes.PROCESS_ORDER ||
            shouldThrottleProcessOrderForSavedCard(checkoutState.payload)
        ) {
            return;
        }
        const { placeOrderData, order, startTime } = checkoutState.payload;
        const { paymentMethodData } = placeOrderData;
        const processOrderStartTime = Date.now();
        processOrder({
            processOrderData: checkoutState.payload,
            params: {
                apiHost,
                tenant: !isEmpty(paymentResponse) ? paymentResponse.tenant : "",
            },
            extendedCustomerId: clientAuthTokenData?.extendedCustomerId || "",
            credOfferLayerDetails: paymentResponse?.credRewardsLayer || {},
            isJuspayAuthTokenApiPublic: true,
            rawClientAuthTokenData: rawClientAuthTokenData || undefined,
            paymentResponse: paymentResponse,
        })
            .then(async (processOrderData: ProcessOrderReturnType | void) => {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                    eventSubType: "useProcessOrder_process_order",
                    type: "PROCESS_ORDER_SUCCESS",
                    processOrderDataId: (processOrderData && processOrderData.orderId) || "--",
                });
                if (!isMountedRef.current) {
                    return;
                }

                if (!processOrderData || !processOrderData.orderId) {
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                        type: "RESET_PROCESS_ORDER",
                    });
                    reset();
                    return;
                }

                if (isBusinessLineCBCC()) {
                    // close webview and redirect to cbcc webview
                    try {
                        await redirectToCBCC();
                    } catch (error) {
                        // Show error message to user
                        if (isMountedRef.current) {
                            showNativeAlert();
                            reset();
                        }
                    }
                    return;
                }

                const {
                    internalRedirect = false,
                    shouldRedirect = false,
                    redirectURI,
                    orderId,
                    paymentPartnerOrderId,
                    isOrderProcessed,
                    processSdkFlow,
                    amazonPayTransactionId,
                    redirectFn,
                    status: processOrderStatus,
                } = processOrderData;

                const isRedirect = internalRedirect || (shouldRedirect && redirectURI);
                const isAmazonPaySdkFlow = processSdkFlow ?? false;

                if (isAmazonPaySdkFlow && amazonPayTransactionId) {
                    try {
                        const params = {
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            order_id: paymentPartnerOrderId,
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            payment_meta: {
                                transactionIdType: "TRANSACTION_ID",
                                transactionId: amazonPayTransactionId,
                            },
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            payment_method: placeOrderData.paymentMethodData.name,
                        };

                        const encryptResponse = await aPaySignAndEncryptPublic(apiHost, params);
                        if (encryptResponse) {
                            const nbStart = Date.now();
                            const chargeStatusNBResponse = (await getBridgeSdk().payUsingAmazonPay({
                                state: AMAZON_PAY_NB_STATES.CHECK_CHARGE_STATUS,
                                data: {
                                    ...encryptResponse,
                                },
                            })) as AmazonSdkChargeStatusResponseData;
                            recordNativeBridgeCall({
                                nbState: AMAZON_PAY_NB_STATES.CHECK_CHARGE_STATUS,
                                isNewSdk: false,
                                status: APAY_NB_CALL_STATUS.SUCCESS,
                                latencyMs: Date.now() - nbStart,
                                extra: {
                                    hasPaymentMeta: String(!!chargeStatusNBResponse?.payment_meta),
                                },
                            });
                            if (chargeStatusNBResponse && chargeStatusNBResponse.payment_meta) {
                                const params = {
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    order_id: paymentPartnerOrderId,
                                    ...chargeStatusNBResponse,
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    payment_method: placeOrderData.paymentMethodData.name,
                                };
                                const response = await aPayValidateTransactionPublic(params);

                                if (response?.data) {
                                    setTimeout(() => {
                                        if (isMountedRef.current && setCheckoutState) {
                                            getBridgeSdk()
                                                .setScreenCaptureState({ screenCapturingDisabled: true })
                                                .catch(() => void 0);

                                            setCheckoutState({
                                                type: CheckoutActionTypes.CONFIRM_ORDER,
                                                payload: {
                                                    orderId,
                                                    isOrderProcessed,
                                                    order,
                                                    placeOrderData,
                                                },
                                            });

                                            setProcessingOrder(false);
                                        }
                                        return;
                                    }, response?.data?.confirmOrderPostDelay);
                                }
                            }
                        }
                    } catch (error) {
                        getBridgeSdk()
                            .setScreenCaptureState({ screenCapturingDisabled: false })
                            .catch(() => void 0);
                        recordNREvent({
                            eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                            eventSubType: "useProcessOrder_amazon_pay_sdk_failure",
                            type: "AMAZON_PAY_SDK_FAILURE",
                            errorName: (error as Error).name,
                            errorMessage: (error as Error).message,
                            isNewSdk: BOOLEAN_AS_STRING.FALSE,
                        });

                        recordNREvent({
                            eventType: NEWRELIC_TABLES.AMAZONPAYSDK_FAILURES,
                            errorName: (error as Error).name,
                            errorMessage: (error as Error).message,
                            isNewSdk: BOOLEAN_AS_STRING.FALSE,
                        });
                    }
                }

                if (!isRedirect) {
                    // if not redirection flow allow recording again else we will allow in confirm order screen
                    getBridgeSdk()
                        .setScreenCaptureState({ screenCapturingDisabled: false })
                        .catch(() => void 0);
                }

                try {
                    // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                        name: "useProcessOrder",
                        isRedirect: String(isRedirect),
                        orderId,
                        paymentPartnerOrderId: paymentPartnerOrderId || "",
                    });
                } catch {
                    /* No-op*/
                }

                if (isRedirect) {
                    // save checkout state context & data in sessionStorage before naviagtion
                    saveProcessOrderState(orderId, checkoutState);
                    setCheckoutState({
                        type: CheckoutActionTypes.SHOW_FULL_PAGE_LOADER,
                    });

                    const backURL = getJuspayBackURL(
                        configValue.paymentReturnUrl?.unwrapOr(""),
                        configValue.addSwiggyMoneyReturnUrl?.unwrapOr(""),
                    );
                    showNativeHeader({
                        title: "MAKE PAYMENT",
                        backURL,
                        backAlert: PAYMENT_BACK_ALERT_OPTIONS,
                    });

                    if (shouldRedirect && redirectURI) {
                        window.location.href = redirectURI;
                    }

                    // call the redirect function if available
                    if (internalRedirect && redirectFn) {
                        redirectFn();
                    }

                    return;
                }

                if (isMountedRef.current) {
                    const methodName = getPlaceOrderPaymentMethodName(checkoutState.payload);
                    if (shouldPollForTxnStatus(order, methodName)) {
                        const responseTime = Date.now() - processOrderStartTime;
                        // when method is UPIIntent, we want to stop polling faster so if upi native bridge gives failure we skip polling step and move to confirm order directly
                        if (
                            isPaymentMethodUpiAndPaymentCodeUpiIntent(placeOrderData.paymentMethodData) &&
                            responseTime > 500 &&
                            processOrderStatus === UPI_NB_STATUS.FAILURE
                        ) {
                            upiPaymentFailedImpressionEvent(impressionEvent, {
                                processOrderStatus,
                                upiAppName: placeOrderData?.paymentMethodData?.name || "--",
                            });

                            setCheckoutState({
                                type: CheckoutActionTypes.CONFIRM_ORDER,
                                payload: {
                                    orderId,
                                    isOrderProcessed,
                                    order,
                                    placeOrderData,
                                },
                            });
                        } else if (
                            isPaymentMethodUpiAndPaymentCodeUpiIntent(placeOrderData.paymentMethodData) &&
                            responseTime > 500 &&
                            processOrderStatus === UPI_NB_STATUS.MWEB_UPI_APP_REDIRECT_FAILURE
                        ) {
                            // NR error log
                            recordNREvent(
                                {
                                    orderId,
                                    eventType: NEWRELIC_TABLES.MWEB_UPI_INTENT_REDIRECT_FAILURE,
                                    upiAppName: paymentMethodData?.displayName || "--",
                                    upiIntentUrl: order.upiIntentUrl || "--",
                                },
                                false,
                            );

                            // analytics error log
                            mwebUpiIntentRedirectFailureImpressionEvent(impressionEvent, {
                                upiAppName: paymentMethodData.displayName || "--",
                                upiIntentUrl: order.upiIntentUrl || "--",
                            });

                            // toast error message
                            const upiAppRedirectErrorMessage = format(
                                MWEB_UPI_APP_REDIRECT_FAILURE_MESSAGE,
                                paymentMethodData.displayName,
                            );

                            setCheckoutState({
                                type: CheckoutActionTypes.GENERIC_PAYMENT_TOAST,
                                payload: {
                                    message: upiAppRedirectErrorMessage,
                                    type: TOAST_TYPE.ERROR,
                                },
                            });
                        } else {
                            setCheckoutState({
                                type: CheckoutActionTypes.PAYMENT_TXN_STATUS_POLLER,
                                payload: { order, placeOrderData, startTime },
                            });
                        }
                    } else {
                        // for cash orders placed from autopay screen also we will redirect
                        // user to confirm order screen
                        setCheckoutState({
                            type: CheckoutActionTypes.CONFIRM_ORDER,
                            payload: {
                                orderId,
                                isOrderProcessed,
                                order,
                                placeOrderData,
                            },
                        });
                    }

                    setProcessingOrder(false);
                }

                Blaze.recordEnd(BLAZE_EVENTS.RECORD_PROCESS_ORDER_LOAD_TIME, {
                    orderPaymentMethod: paymentMethodData?.paymentCode || "",
                    groupName: paymentMethodData?.groupName || "",
                    orderId,
                });
            })
            .catch((err) => {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                    eventSubType: "useProcessOrder_process_order_failure",
                    type: "PROCESS_ORDER_FAILURE",
                    errorName: err.name,
                    errorMessage: err.message,
                });
                getBridgeSdk()
                    .setScreenCaptureState({ screenCapturingDisabled: false })
                    .catch(() => void 0);

                sendOrderFailedEvent({
                    placeOrderData,
                    orderId: String(order.orderId),
                    totalBill: String(order.orderTotal),
                    error: err,
                    failInstance: ORDER_FAIL_INSTANCE.PROCESS,
                });
                captureError(err);
                if (isMountedRef.current) {
                    hideNativeHeader();
                    showNativeAlert();
                    reset();
                }
            });
    }, [
        processingOrder,
        checkoutState,
        isMountedRef,
        setCheckoutState,
        reset,
        apiHost,
        paymentResponse,
        configValue.paymentReturnUrl,
        configValue.addSwiggyMoneyReturnUrl,
        impressionEvent,
        clientAuthTokenData?.extendedCustomerId,
        rawClientAuthTokenData,
    ]);

    return [processingOrder];
};

export default useProcessOrder;
